import { useCallback, useState, useMemo } from 'react';
import { Box, Grid, Container, Stack, Button, Typography, Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { BiCloudUpload } from 'react-icons/bi';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import AutorenewIcon from '@mui/icons-material/Autorenew';

import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import BrudCrumbs from '../../components/BreadCrumbs';
import CustomInput from '../../components/CustomInput';
import { savefileApiData } from '../../store/Slice/Global/GlobalSlice';
import { DataSourceApi } from '../../utils/Apis';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import CustomSelect from '../../components/CustomSelect';
import useDataSourceSelector from '../../store/Slice/DataSource/DataSourceSelector';
import { pillTabsSx } from '../Global/Styling';

const P = '#13519d';

const STATIC_TABS = [
  { id: 1, label: 'Add New', icon: <AddCircleIcon /> },
  { id: 2, label: 'Append', icon: <PlaylistAddIcon /> },
  { id: 3, label: 'Rewrite', icon: <AutorenewIcon /> }
];

function FileUploadSection({ control, setValue, fileValue, errors }) {
  return (
    <Controller
      name="file"
      control={control}
      rules={{ required: 'File is required' }}
      render={() => (
        <Box sx={{ height: '100%' }}>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{
              py: 4,
              border: '2.5px dashed #cbd5e1',
              color: '#64748b',
              bgcolor: '#f8fafc',
              '&:hover': {
                bgcolor: '#f0f7ff',
                border: '2.5px dashed #13519d',
                color: '#13519d'
              },
              textTransform: 'none',
              borderRadius: '12px',
              flexDirection: 'column',
              gap: 1.5,
              position: 'relative',
              height: '100%',
              minHeight: 180
            }}
          >
            <BiCloudUpload size={42} style={{ marginBottom: 2, color: '#13519d' }} />
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.05rem',
                color: fileValue?.name ? '#13519d' : '#1e293b'
              }}
            >
              {fileValue?.name || 'Select Supporting File'}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
              Supported file types: .xls, .xlsx, .csv — Max 10MB
            </Typography>
            <input
              accept=".xlsx, .xls, .csv"
              hidden
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setValue('file', file, { shouldValidate: true });
                e.target.value = null;
              }}
            />
          </Button>

          {errors?.file && (
            <Typography color="error" variant="caption" display="block" mt={1}>
              {errors.file.message}
            </Typography>
          )}
        </Box>
      )}
    />
  );
}

FileUploadSection.propTypes = {
  control: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  fileValue: PropTypes.object,
  errors: PropTypes.object
};

export default function DataAdd() {
  const [fileTypeId, setFileTypeId] = useState(1);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { LoginData } = useLoginSelector();
  const { DataSourceListData } = useDataSourceSelector();

  const token = LoginData?.jwt;
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      datasetName: '',
      datasetDescription: '',
      file: null
    }
  });

  const handleChangeTab = useCallback(
    (_, newValue) => {
      setFileTypeId(newValue);
      reset({
        datasetName: '',
        datasetDescription: '',
        file: null
      });
    },
    [reset]
  );

  const fileValue = watch('file');

  const onSubmit = useCallback(
    async (data) => {
      const formData = new FormData();

      // Decide purpose based on selected tab
      let purpose = '';
      if (fileTypeId === 1) purpose = 'ADD_NEW';
      if (fileTypeId === 2) purpose = 'APPEND';
      if (fileTypeId === 3) purpose = 'REWRITE';

      formData.append('purpose', purpose);

      // ---------- TAB-1 : ADD NEW ----------
      if (fileTypeId === 1) {
        formData.append('datasetName', data.datasetName);
        formData.append('datasetDescription', data.datasetDescription || '');
      }

      // ---------- TAB-2 / TAB-3 : APPEND / REWRITE ----------
      if (fileTypeId === 2 || fileTypeId === 3) {
        const selected = DataSourceListData?.find((x) => x.id === data.datasetName);

        formData.append('datasetId', data.datasetName);
        formData.append('datasetName', selected?.datasetName);

        if (fileTypeId === 3) {
          formData.append('datasetDescription', data.datasetDescription || '');
        }
      }

      // ---------- FILE ----------
      if (data.file) {
        formData.append('file', data.file);
      }

      try {
        const res = await dispatch(
          savefileApiData(`${DataSourceApi?.dataSourceUpload}`, formData, token)
        );

        if (res?.success === true) navigate('/data');
      } catch (err) {
        console.error('Submit failed:', err);
      }
    },
    [dispatch, navigate, fileTypeId, DataSourceListData, token]
  );

  const selectOptions = useMemo(
    () =>
      DataSourceListData?.map((item) => ({
        label: item.datasetName,
        value: item.id
      })) || [],
    [DataSourceListData]
  );

  return (
    <Page title="Partner Management System || Add Data Source">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
            className="pageHeading"
          >
            <PageTitle info="Add Data Source" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Tabs
              value={fileTypeId}
              onChange={handleChangeTab}
              textColor="primary"
              indicatorColor="primary"
              sx={pillTabsSx}
            >
              {STATIC_TABS.map((tab) => (
                <Tab
                  key={tab.id}
                  value={tab.id}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                />
              ))}
            </Tabs>
          </Box>

          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 4px 14px rgba(0,0,0,0.05)',
              mb: 3,
              mt: 3
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, ${P} 0%, #1565c0 100%)`,
                px: { xs: 2, sm: 3.5 },
                pt: { xs: 2, sm: 3 },
                pb: { xs: 2.5, sm: 3.5 },
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography
                  sx={{ fontWeight: 900, fontSize: { xs: '1.2rem', sm: '1.4rem' }, color: '#fff' }}
                >
                  Fill Details
                </Typography>
              </Box>
            </Box>

            <Box sx={{ p: { xs: 2, md: 4 } }}>
              {/* -------- TAB 1 : ADD NEW -------- */}
              <TabPanel value={fileTypeId} index={1}>
                <form id="addDataSourceForm1" onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <CustomInput
                            name="datasetName"
                            label="Dataset Name"
                            control={control}
                            errors={errors}
                            isRequired
                            fieldType="TEXT_FIELD"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <CustomInput
                            name="datasetDescription"
                            label="Dataset Description"
                            type="text"
                            control={control}
                            errors={errors}
                            isRequired
                            fieldType="TEXT_FIELD"
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FileUploadSection
                        control={control}
                        setValue={setValue}
                        fileValue={fileValue}
                        errors={errors}
                      />
                    </Grid>
                  </Grid>
                </form>
              </TabPanel>

              {/* -------- TAB 2 : APPEND -------- */}
              <TabPanel value={fileTypeId} index={2}>
                <form id="addDataSourceForm2" onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <CustomSelect
                            name="datasetName"
                            label="Dataset Name"
                            control={control}
                            errors={errors}
                            options={selectOptions || []}
                            isRequired
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FileUploadSection
                        control={control}
                        setValue={setValue}
                        fileValue={fileValue}
                        errors={errors}
                      />
                    </Grid>
                  </Grid>
                </form>
              </TabPanel>

              {/* -------- TAB 3 : REWRITE -------- */}
              <TabPanel value={fileTypeId} index={3}>
                <form id="addDataSourceForm3" onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <CustomSelect
                            name="datasetName"
                            label="Dataset Name"
                            control={control}
                            errors={errors}
                            options={selectOptions || []}
                            isRequired
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <CustomInput
                            name="datasetDescription"
                            label="Dataset Description"
                            type="text"
                            control={control}
                            errors={errors}
                            isRequired
                            fieldType="TEXT_FIELD"
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FileUploadSection
                        control={control}
                        setValue={setValue}
                        fileValue={fileValue}
                        errors={errors}
                      />
                    </Grid>
                  </Grid>
                </form>
              </TabPanel>
            </Box>
          </Box>

          <Box textAlign="right">
            <Button
              type="submit"
              form={`addDataSourceForm${fileTypeId}`}
              variant="contained"
              sx={{
                bgcolor: P,
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                px: 4,
                py: 1,
                boxShadow: '0 4px 14px rgba(13,70,137,0.25)',
                '&:hover': { bgcolor: '#0a3870' }
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
};
